import * as Sentry from "@sentry/react";

let sentryInitialized = false;

/**
 * Initializes Sentry for error tracking and performance monitoring.
 * Deferred initialization: called after first render to avoid blocking FCP/LCP.
 * Only active in production mode (`import.meta.env.PROD`).
 *
 * Called from useDeferredSentryInit() which fires after FCP (via requestIdleCallback).
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const isProd = import.meta.env.PROD;

  // Avoid double-init
  if (sentryInitialized) {
    return;
  }

  if (!isProd) {
    // Do not initialize Sentry in development or test environments
    return;
  }

  if (!dsn) {
    console.warn("[Sentry] VITE_SENTRY_DSN is missing in production environment.");
    return;
  }

  sentryInitialized = true;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE || "production",
    // Release = commit SHA (CI inyecta VITE_GIT_SHA) para correlacionar
    // issues ↔ commits ↔ source maps subidas por sentry-cli.
    release: import.meta.env.VITE_GIT_SHA || undefined,
    enabled: true,
    // Replay is intentionally disabled from integrations during initial load.
    // It will be added after first render to avoid blocking FCP.
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    // Performance Monitoring: sample 20% of transactions in prod
    tracesSampleRate: 0.2,
    // Session Replay: deferred until after first render (see useDeferredSentryInit)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    // Ruido conocido no accionable (no son bugs de nuestra app).
    // NOTA: "Script error." NO está aquí (ver beforeSend): los errores opacos
    // procedentes de js.stripe.com deben llegar para diagnosticar fallos de
    // Stripe.js en móviles (incidente Android 10 / Samsung Internet 30).
    ignoreErrors: [
      "ResizeObserver loop",
      "The user aborted a request.",
      "NetworkError when attempting to fetch resource",
      // Extensiones de navegador Chromium (traducción, autofill, etc.) que pierden el canal IPC
      /Object Not Found Matching Id/i,
      // Google Translate / extensiones que alteran nodos de texto causando desincronización en React DOM (removeChild / insertBefore)
      /Failed to execute 'removeChild' on 'Node'/i,
      /The node to be removed is not a child of this node/i,
      /Failed to execute 'insertBefore' on 'Node'/i,
    ],
    // Errores lanzados por scripts de terceros (Google, Meta).
    // js.stripe.com NO se deniega: sus errores son críticos para el funnel
    // de pago y deben ser visibles (el preload con crossorigin en index.html
    // los hace legibles en vez de "Script error." opacos).
    denyUrls: [
      /googleapis\.com|google\.com/,
      /doubleclick\.net/,
      /facebook\.com|fbcdn\.net/,
    ],
    beforeSend(event) {
      // Filtrar los "Script error." opacos de terceros (sin CORS), que antes
      // descartaba ignoreErrors, PERMITIENDO pasar los de Stripe:
      //   - etiquetados con area: stripe-js / stripe-payment (nuestras
      //     capturas explícitas del funnel de pago), o
      //   - con al menos un frame en js.stripe.com.
      const isOpaqueScriptError =
        event.message === "Script error." ||
        (event.exception?.values ?? []).some((v) => v.value === "Script error.");
      if (isOpaqueScriptError) {
        const tags = event.tags ?? {};
        const fromStripeFrames = (event.exception?.values ?? []).some((v) =>
          (v.stacktrace?.frames ?? []).some((f) =>
            /js\.stripe\.com/.test(f.filename ?? f.abs_path ?? "")
          )
        );
        const fromStripeTags =
          tags["area"] === "stripe-js" || tags["area"] === "stripe-payment";
        if (!fromStripeFrames && !fromStripeTags) {
          return null; // ruido de terceros opaco → descartar
        }
        event.tags = { ...tags, "opaque-script-error": "true" };
      }
      return event;
    },
  });
}

/**
 * Enable Sentry Replay integration after first render (deferred).
 * Called from App component's useEffect to add Replay capture to active sessions.
 * This avoids loading Replay's 50-70 KB during initial critical path (FCP/LCP).
 */
export function enableSentryReplayDeferred(): void {
  const isProd = import.meta.env.PROD;
  if (!isProd || !sentryInitialized) {
    return;
  }

  try {
    // Add replay integration to the active Sentry client
    const client = Sentry.getClient();
    if (client && !client.getIntegrationByName("Replay")) {
      client.addIntegration(
        Sentry.replayIntegration({
          // Session Replay: sample 10% normal sessions, 100% error sessions
          maskAllText: true,
          blockAllMedia: true,
        })
      );
    }
  } catch (e) {
    if (import.meta.env.DEV) {
      console.warn("[Sentry] Failed to enable Replay deferred:", e);
    }
  }
}

